import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildTapLogQuery = (state) => {
  const { filters, pagination, sort } = state.tapLogs;
  const params = new URLSearchParams();
  params.append("page", pagination.page + 1);
  params.append("pageSize", pagination.pageSize);
  if (filters.search.trim()) {
    params.append("search", filters.search.trim());
  }
  if (filters.eventId) {
    params.append("eventId", filters.eventId);
  }
  if (filters.startDate) {
    params.append("startDate", formatJakartaStartOfDay(filters.startDate));
  }
  if (filters.endDate) {
    params.append("endDate", formatJakartaEndOfDay(filters.endDate));
  }
  if (sort.field) {
    params.append("sortBy", sort.field);
    params.append("sortDirection", sort.direction);
  }
  return params.toString();
};

export const fetchTapLogs = createAsyncThunk(
  "tapLogs/fetchList",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildTapLogQuery(state);
      const response = await fetch(`${API_BASE_URL}/tap-logs?${query}`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load tap logs.");
    }
  }
);

export const fetchTapLogEvents = createAsyncThunk(
  "tapLogs/fetchEvents",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events?all=true&sortBy=created_datetime&sortDirection=desc`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load events.");
    }
  }
);

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const initialState = {
  items: [],
  meta: initialMeta,
  pagination: {
    page: 0,
    pageSize: 10,
  },
  filters: {
    search: "",
    eventId: "",
    startDate: null,
    endDate: null,
  },
  sort: {
    field: "tap_datetime",
    direction: "desc",
  },
  loading: false,
  error: null,
  events: [],
  eventsStatus: "idle",
  eventsError: null,
};

const tapLogsSlice = createSlice({
  name: "tapLogs",
  initialState,
  reducers: {
    setTapLogSearch: (state, action) => {
      state.filters.search = action.payload || "";
      state.pagination.page = 0;
    },
    setTapLogEvent: (state, action) => {
      state.filters.eventId = action.payload || "";
      state.pagination.page = 0;
    },
    setTapLogDateRange: (state, action) => {
      state.filters.startDate = action.payload.startDate || null;
      state.filters.endDate = action.payload.endDate || null;
      state.pagination.page = 0;
    },
    setTapLogPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setTapLogPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
    setTapLogSort: (state, action) => {
      const { field, direction } = action.payload || {};
      if (!field) return;
      state.sort = {
        field,
        direction: direction === "asc" ? "asc" : "desc",
      };
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTapLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTapLogs.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const items =
          payload.items ||
          payload.data?.items ||
          (Array.isArray(payload) ? payload : []);
        const meta = payload.meta || payload.data?.meta || {};
        state.items = Array.isArray(items) ? items : [];
        state.meta = {
          ...initialMeta,
          ...meta,
        };
        if (typeof state.meta.page === "number") {
          state.pagination.page = Math.max(state.meta.page - 1, 0);
        }
        if (typeof state.meta.pageSize === "number") {
          state.pagination.pageSize = state.meta.pageSize;
        }
      })
      .addCase(fetchTapLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load tap logs.";
      })
      .addCase(fetchTapLogEvents.pending, (state) => {
        state.eventsStatus = "loading";
        state.eventsError = null;
      })
      .addCase(fetchTapLogEvents.fulfilled, (state, action) => {
        state.eventsStatus = "succeeded";
        const payload = action.payload;
        const list =
          payload?.items ||
          payload?.data?.items ||
          payload?.data ||
          payload ||
          [];
        state.events = Array.isArray(list) ? list : [];
      })
      .addCase(fetchTapLogEvents.rejected, (state, action) => {
        state.eventsStatus = "failed";
        state.eventsError = action.payload || "Failed to load events.";
      });
  },
});

export const {
  setTapLogSearch,
  setTapLogEvent,
  setTapLogDateRange,
  setTapLogPage,
  setTapLogPageSize,
  setTapLogSort,
} = tapLogsSlice.actions;

export const selectTapLogs = (state) => state.tapLogs.items;
export const selectTapLogsMeta = (state) => state.tapLogs.meta;
export const selectTapLogsPagination = (state) => state.tapLogs.pagination;
export const selectTapLogsFilters = (state) => state.tapLogs.filters;
export const selectTapLogsSort = (state) => state.tapLogs.sort;
export const selectTapLogsLoading = (state) => state.tapLogs.loading;
export const selectTapLogsError = (state) => state.tapLogs.error;
export const selectTapLogEvents = (state) => state.tapLogs.events;
export const selectTapLogEventsStatus = (state) => state.tapLogs.eventsStatus;

export default tapLogsSlice.reducer;
